<template>
  <scoped>
    <table>
      <thead>
        <tr>
          <th v-if="!readOnly">
            <Button
              title="Add"
              @click="displayAdd = true"
            />
          </th>
          <th>
            Preview
          </th>
          <th>
            Type
          </th>
          <th>
            Name
          </th>
          <th>
            Extension
          </th>
          <th>
            Description
          </th>
        </tr>
      </thead>
      <tbody v-if="items.length > 0">
        <template
          v-for="(file, index) in items"
          :key="file.name + file.extension"
        >
          <tr
            v-if="!file.deletedAt"
            :draggable="!readOnly"
            @click="select(index)"
            @dragstart="dragStart($event, index)"
            @drop="drop($event, index)"
            @dragenter.prevent
            @dragover.prevent
          >
            <td v-if="!readOnly">
              <Button
                title="Remove"
                @click="remove($event, index)"
              />
            </td>
            <td>
              <template v-if="file.type == 'DOCUMENT'">
                <DocumentMedia
                  :data="file.data"
                  :extension="file.extension"
                  :title="file.name"
                  :url="file.url"
                />
              </template>
              <template v-if="file.type == 'PICTURE'">
                <ImageMedia
                  :data="file.data"
                  :extension="file.extension"
                  :title="file.name"
                  :url="file.url"
                />
              </template>
              <template v-if="file.type == 'VIDEO'">
                <VideoMedia
                  :data="file.data"
                  :extension="file.extension"
                  :title="file.name"
                  :url="file.url"
                />
              </template>
            </td>
            <td>
              {{ file.type }}
            </td>
            <td>
              {{ file.name }}
            </td>
            <td>
              {{ file.extension }}
            </td>
            <td>
              {{ file.description }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <ContentModal v-if="displayAdd">
      <Enumeration
        v-model:selection="selectedType"
        title="Type"
        :options="['DOCUMENT', 'PICTURE', 'VIDEO']"
      />
      <FileInput
        v-if="!readOnly"
        ref="fileInput"
        title="Add"
        multiple
        @update="add()"
      />
      <br>
      <Button
        title="Close"
        @click="displayAdd = false"
      />
    </ContentModal>
  </scoped>
</template>

<script setup lang="ts">
  import DocumentMedia from "@/components/media/document.vue"
  import ImageMedia from "@/components/media/image.vue"
  import VideoMedia from "@/components/media/video.vue"
  import ContentModal from "@/components/modals/content.vue"
  import FileInput from "@/components/inputs/file.vue"
  import Button from "@/components/button.vue"
  import {File} from "@/endpoints/rutsolve/gql/generated/graphql"
  import {nextTick, ref, useTemplateRef} from "vue"
  import Enumeration from "@/components/selectors/enumeration.vue"

  defineProps({
    readOnly: {
      default: false,
      required: false,
      type: Boolean
    }
  })

  const emits = defineEmits(["select"])

  const items = defineModel("items", {
    default: null,
    required: true,
    type: Array<File>
  })
  
  const selectedIndex = defineModel("selectedIndex", {
    default: null,
    required: false,
    type: Number
  })

  const fileInput = useTemplateRef("fileInput")

  const displayAdd = ref<boolean>(null)
  const selectedType = ref<string>(null)

  async function select(index: number) {
    selectedIndex.value = index
    await nextTick()
    emits("select")
  }

  function dragStart(event: DragEvent, index: number) {
    event.dataTransfer.dropEffect = "move"
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("INDEX", index.toString())
  }

  function drop(event: DragEvent, index: number) {
    const startIndex = parseInt(event.dataTransfer.getData("INDEX"))
    items.value.splice(index, 0, items.value.splice(startIndex, 1)[0])
    updatePriority()
  }

  function updatePriority() {
    for (const [index, file] of items.value.entries()) {
      file.priority = index
    }
  }

  function remove(event: MouseEvent, index: number) {
    event.stopPropagation()
    items.value[index].deletedAt = new Date().toISOString()
  }

  function add() {
    for (const file of fileInput.value.files) {
      items.value.push({
        data: file.data,
        extension: file.extension,
        name: file.name,
        type: selectedType.value
      })
    }
    updatePriority()
    displayAdd.value = false
  }
</script>

<style scoped>
  table {
    > tbody {
      > tr {
        &:hover {
          background-color: var(--theme-color-secondary);
          color: var(--color-white);
          cursor: pointer;
        }

        > td {
          > :deep(img), :deep(object), :deep(video) {
            max-height: 5em;
          }
        }
      }
    }
  }
</style>
